// Killip Classification — post-AMI 30-day mortality risk stratification.
// Sources: Killip T & Kimball JT. Am J Cardiol 1967; validated in modern
// reperfusion-era registries (GRACE, TRIUMPH).

import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Heart, ArrowUpRight, Siren, ShieldAlert, CheckCircle2 } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageProvider";
import { cn } from "@/lib/utils";

const CLASSES = [
  {
    level: 1,
    roman: "I",
    color: "teal",
    mortality: "≈ 6%",
    en: {
      title: "Class I",
      subtitle: "No signs of heart failure",
      criteria: [
        "No rales / crackles on auscultation",
        "No S3 gallop",
        "No jugular venous distension",
      ],
      recommendation: "Standard post-MI care: reperfusion, DAPT, β-blocker, ACEi/ARB, high-intensity statin. Continue cardiac monitoring.",
    },
    id: {
      title: "Kelas I",
      subtitle: "Tanpa tanda gagal jantung",
      criteria: [
        "Tidak ada ronki pada auskultasi",
        "Tidak ada S3 gallop",
        "Tidak ada peningkatan JVP",
      ],
      recommendation: "Perawatan pasca-MI standar: reperfusi, DAPT, β-blocker, ACEi/ARB, statin intensitas tinggi. Lanjutkan monitor jantung.",
    },
  },
  {
    level: 2,
    roman: "II",
    color: "amber",
    mortality: "≈ 17%",
    en: {
      title: "Class II",
      subtitle: "Mild-to-moderate heart failure",
      criteria: [
        "Rales in < 50% of lung fields",
        "S3 gallop present",
        "Elevated JVP possible",
      ],
      recommendation: "Add loop diuretic (furosemide 20–40 mg IV). Optimise oxygenation. Reassess volume status frequently; escalate if worsening.",
    },
    id: {
      title: "Kelas II",
      subtitle: "Gagal jantung ringan-sedang",
      criteria: [
        "Ronki pada < 50% lapangan paru",
        "S3 gallop teraba",
        "JVP dapat meningkat",
      ],
      recommendation: "Tambahkan diuretik loop (furosemide 20–40 mg IV). Optimalkan oksigenasi. Nilai ulang status volume sering; eskalasi bila memburuk.",
    },
  },
  {
    level: 3,
    roman: "III",
    color: "orange",
    mortality: "≈ 38%",
    en: {
      title: "Class III",
      subtitle: "Overt pulmonary oedema",
      criteria: [
        "Rales in > 50% of lung fields",
        "Frank pulmonary oedema",
        "Severe dyspnoea, hypoxaemia",
      ],
      recommendation: "Aggressive IV loop diuretic; consider IV nitrate (if SBP ≥ 110). Non-invasive ventilation (CPAP/BiPAP) early. ICU/CCU admission.",
    },
    id: {
      title: "Kelas III",
      subtitle: "Edema paru berat",
      criteria: [
        "Ronki pada > 50% lapangan paru",
        "Edema paru overt",
        "Sesak berat, hipoksemia",
      ],
      recommendation: "Diuretik loop IV agresif; pertimbangkan nitrat IV (bila TDS ≥ 110). Ventilasi non-invasif (CPAP/BiPAP) dini. Rawat ICU/CCU.",
    },
  },
  {
    level: 4,
    roman: "IV",
    color: "red",
    mortality: "≈ 81%",
    en: {
      title: "Class IV",
      subtitle: "Cardiogenic shock",
      criteria: [
        "SBP < 90 mmHg",
        "Signs of hypoperfusion (cold, oliguria, altered mentation)",
        "May have pulmonary oedema",
      ],
      recommendation: "Emergency vasopressor/inotrope support. Consider mechanical circulatory support (IABP, Impella, VA-ECMO). Urgent revascularisation. ICU/CCU.",
    },
    id: {
      title: "Kelas IV",
      subtitle: "Syok kardiogenik",
      criteria: [
        "TDS < 90 mmHg",
        "Tanda hipoperfusi (dingin, oliguria, penurunan kesadaran)",
        "Dapat disertai edema paru",
      ],
      recommendation: "Dukungan vasopresor/inotropik darurat. Pertimbangkan bantuan sirkulasi mekanik (IABP, Impella, VA-ECMO). Revaskularisasi urgent. ICU/CCU.",
    },
  },
];

const COLORS = {
  teal: {
    ring: "border-teal-700/50 hover:border-teal-700",
    active: "border-teal-700 bg-teal-700/10 ring-2 ring-teal-700",
    icon: "text-teal-700",
    pill: "bg-teal-700 text-white",
    result: "border-teal-700/50 bg-teal-700/5",
  },
  amber: {
    ring: "border-amber-600/50 hover:border-amber-600",
    active: "border-amber-600 bg-amber-500/10 ring-2 ring-amber-600",
    icon: "text-amber-700",
    pill: "bg-amber-600 text-white",
    result: "border-amber-600/50 bg-amber-500/10",
  },
  orange: {
    ring: "border-orange-600/50 hover:border-orange-600",
    active: "border-orange-600 bg-orange-500/10 ring-2 ring-orange-600",
    icon: "text-orange-700",
    pill: "bg-orange-600 text-white",
    result: "border-orange-600/50 bg-orange-500/10",
  },
  red: {
    ring: "border-red-600/50 hover:border-red-600",
    active: "border-red-600 bg-red-500/10 ring-2 ring-red-600",
    icon: "text-red-700",
    pill: "bg-red-600 text-white",
    result: "border-red-600/60 bg-red-500/10",
  },
};

export default function Killip() {
  const { lang } = useLanguage();
  const [selected, setSelected] = useState(null);

  const T = (obj) => (lang === "id" ? obj.id : obj.en);
  const isID = lang === "id";
  const result = selected ? CLASSES.find((c) => c.level === selected) : null;
  const resultC = result ? COLORS[result.color] : null;

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-10 py-8 md:py-12" data-testid="killip-page">
      <header className="border-b border-stone-200 pb-5 mb-8">
        <div className="text-[11px] tracking-[0.18em] uppercase font-semibold text-teal-700 mb-2 flex items-center gap-2">
          <Heart size={14} strokeWidth={1.5} />
          {isID ? "Kardiologi · Stratifikasi Risiko" : "Cardiology · Risk Stratification"}
        </div>
        <h1 className="font-display text-3xl md:text-4xl font-medium text-stone-900 tracking-tight leading-tight">
          {isID ? "Klasifikasi Killip" : "Killip Classification"}
        </h1>
        <p className="mt-3 text-sm md:text-base text-stone-600 leading-relaxed max-w-2xl">
          {isID
            ? "Stratifikasi risiko mortalitas 30 hari pasca-infark miokard akut berdasarkan pemeriksaan fisik samping tempat tidur."
            : "30-day mortality risk stratification after acute myocardial infarction, based on bedside physical examination."}
        </p>
      </header>

      <div className="mb-4 text-xs font-mono text-stone-500 tracking-wider uppercase">
        {isID ? "Pilih kelas berdasarkan temuan pasien" : "Select class based on patient findings"}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="killip-cards">
        {CLASSES.map((c) => {
          const colors = COLORS[c.color];
          const isActive = selected === c.level;
          const content = T(c);
          return (
            <button
              key={c.level}
              type="button"
              onClick={() => setSelected(c.level)}
              data-testid={`killip-class-${c.roman.toLowerCase()}`}
              aria-pressed={isActive}
              className={cn(
                "text-left border-2 rounded-md p-5 bg-white transition-all min-h-[140px]",
                isActive ? colors.active : colors.ring
              )}
            >
              <div className="flex items-start justify-between">
                <div className={cn("h-11 w-11 rounded-full flex items-center justify-center text-lg font-display font-medium", colors.pill)}>
                  {c.roman}
                </div>
                <span className="text-[10px] tracking-[0.16em] uppercase font-mono text-stone-500">
                  {isID ? "Mortalitas 30 hari" : "30-day mortality"}: <span className="font-semibold text-stone-800">{c.mortality}</span>
                </span>
              </div>
              <div className="mt-4">
                <div className="font-display text-lg font-medium text-stone-900">{content.title}</div>
                <div className={cn("text-sm font-medium mt-0.5", colors.icon)}>{content.subtitle}</div>
              </div>
              <ul className="mt-3 space-y-1 text-xs text-stone-700 leading-relaxed">
                {content.criteria.map((crit, i) => (
                  <li key={i} className="flex gap-1.5">
                    <span className="text-stone-400 mt-0.5">•</span>
                    <span>{crit}</span>
                  </li>
                ))}
              </ul>
            </button>
          );
        })}
      </div>

      {result && (
        <section
          data-testid="killip-result"
          className={cn("mt-8 border-2 rounded-md p-6", resultC.result)}
        >
          <div className="flex items-center gap-3 mb-4">
            <div className={cn("h-10 w-10 rounded-full flex items-center justify-center font-display", resultC.pill)}>
              {result.roman}
            </div>
            <div>
              <div className="text-[11px] tracking-[0.18em] uppercase font-semibold text-stone-500">
                {isID ? "Hasil" : "Result"}
              </div>
              <div className="font-display text-xl font-medium text-stone-900">
                {T(result).title} — {T(result).subtitle}
              </div>
            </div>
            <div className="ml-auto text-right">
              <div className="text-[10px] font-mono uppercase tracking-wider text-stone-500">
                {isID ? "Estimasi mortalitas 30 hari" : "Est. 30-day mortality"}
              </div>
              <div className={cn("font-display text-2xl font-semibold", resultC.icon)}>{result.mortality}</div>
            </div>
          </div>

          <div className="border-t border-current/20 pt-4">
            <div className="text-[11px] tracking-[0.18em] uppercase font-semibold text-stone-500 mb-2 flex items-center gap-2">
              <ShieldAlert size={14} strokeWidth={1.5} />
              {isID ? "Rekomendasi klinis" : "Clinical recommendation"}
            </div>
            <p className="text-sm text-stone-800 leading-relaxed">{T(result).recommendation}</p>
          </div>

          {result.level === 4 && (
            <Link
              to="/emergency-drugs"
              data-testid="killip-shortcut-emergency"
              className="mt-5 flex items-center justify-between gap-3 border-2 border-red-600 bg-red-600 text-white hover:bg-red-700 rounded-md px-5 py-4 transition-colors"
            >
              <div className="flex items-center gap-3">
                <Siren size={20} strokeWidth={1.75} />
                <div>
                  <div className="font-semibold">
                    {isID ? "Buka Kalkulator Vasopresor" : "Open Vasopressor Calculator"}
                  </div>
                  <div className="text-xs opacity-90 mt-0.5">
                    {isID
                      ? "Norepinefrin, Epinefrin, Dobutamin — perhitungan mcg/kg/min instan."
                      : "Norepinephrine, Epinephrine, Dobutamine — instant mcg/kg/min."}
                  </div>
                </div>
              </div>
              <ArrowUpRight size={20} strokeWidth={1.75} />
            </Link>
          )}
        </section>
      )}

      {!result && (
        <div
          className="mt-8 border border-dashed border-stone-300 rounded-md p-8 text-center text-sm text-stone-500"
          data-testid="killip-empty"
        >
          <CheckCircle2 size={20} strokeWidth={1.5} className="mx-auto mb-2 text-stone-400" />
          {isID ? "Pilih salah satu kelas di atas untuk melihat rekomendasi klinis." : "Select a class above to see the clinical recommendation."}
        </div>
      )}

      <div className="mt-10 max-w-2xl">
        <div className="border-l-2 border-amber-700/60 pl-4 py-1 text-xs text-stone-600 leading-relaxed">
          <span className="font-semibold text-stone-900">
            {isID ? "Sebagai rujukan saja." : "Reference only."}
          </span>{" "}
          {isID
            ? "Angka mortalitas berasal dari registri era reperfusi (GRACE, TRIUMPH). Selalu ikuti protokol RS setempat dan pertimbangkan konteks pasien secara individual."
            : "Mortality rates come from reperfusion-era registries (GRACE, TRIUMPH). Always follow local hospital protocol and consider individual patient context."}
        </div>
      </div>
    </div>
  );
}
